import { useMemo, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface UngroupedSku {
  id: string;
  sku: string;
  title: string | null;
  imageUrl: string | null;
  size: string | null;
  color: string | null;
  category: string;
}

interface ProposedGroup {
  prefix: string;
  title: string;
  imageUrl: string | null;
  skuIds: string[];
  skus: UngroupedSku[];
  enabled: boolean;
}

interface AutoGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  ungroupedSkus: UngroupedSku[];
  onGrouped: () => void | Promise<void>;
}

export default function AutoGroupModal({
  isOpen,
  onClose,
  ungroupedSkus,
  onGrouped,
}: AutoGroupModalProps) {
  const [expandedPrefix, setExpandedPrefix] = useState<string | null>(null);

  // Compute proposed groups from ungrouped PAP SKUs: group by title (all variants of
  // the same Shopify product share a title). POD blanks group themselves by code and
  // the server refuses a POD request, so this modal is PAP only. A SKU with no title
  // has nothing to group by and stays in the ungrouped list.
  const defaultGroups = useMemo(() => {
    const groupMap = new Map<string, UngroupedSku[]>();

    for (const sku of ungroupedSkus) {
      if (!sku.title) continue;
      const existing = groupMap.get(sku.title) ?? [];
      existing.push(sku);
      groupMap.set(sku.title, existing);
    }

    return Array.from(groupMap.entries())
      .map(([key, skus]): ProposedGroup => ({
        prefix: key,
        title: key,
        imageUrl: skus.find((s) => s.imageUrl)?.imageUrl ?? null,
        skuIds: skus.map((s) => s.id),
        skus,
        enabled: true,
      }))
      .sort((a, b) => a.prefix.localeCompare(b.prefix));
  }, [ungroupedSkus]);

  const [groups, setGroups] = useState<ProposedGroup[]>(defaultGroups);

  // Sync when modal opens with fresh data
  const prevUngroupedRef = useMemo(() => ungroupedSkus, [ungroupedSkus]);
  if (prevUngroupedRef === ungroupedSkus && groups.length === 0 && defaultGroups.length > 0) {
    setGroups(defaultGroups);
  }

  // Reset when modal opens
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setGroups(defaultGroups);
      setExpandedPrefix(null);
    } else {
      onClose();
    }
  };

  const updateGroupTitle = (prefix: string, newTitle: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.prefix === prefix ? { ...g, title: newTitle } : g)),
    );
  };

  const toggleGroup = (prefix: string) => {
    setGroups((prev) =>
      prev.map((g) => (g.prefix === prefix ? { ...g, enabled: !g.enabled } : g)),
    );
  };

  const enabledGroups = groups.filter((g) => g.enabled);
  const totalSkus = enabledGroups.reduce((sum, g) => sum + g.skuIds.length, 0);
  // Only the SKUs that made a group: untitled ones are not proposed, so they are not "detected".
  const detectedSkus = groups.reduce((sum, g) => sum + g.skuIds.length, 0);

  const mutation = trpc.inventory.bulkAutoGroupSkus.useMutation({
    onSuccess: async (result) => {
      toast.success(
        `Created ${result.created} product${result.created !== 1 ? 's' : ''} from ${totalSkus} SKUs`,
      );
      onClose();
      await onGrouped();
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to auto-group SKUs');
    },
  });

  const canSubmit = enabledGroups.length > 0 && enabledGroups.every((g) => g.title.trim().length > 0) && !mutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    mutation.mutate({
      groups: enabledGroups.map((g) => ({
        title: g.title.trim(),
        imageUrl: g.imageUrl ?? undefined,
        skuIds: g.skuIds,
      })),
      productionType: 'PAP',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Auto-group by product title</DialogTitle>
          <DialogDescription>
            {groups.length} group{groups.length !== 1 ? 's' : ''} detected from{' '}
            {detectedSkus} ungrouped SKUs. Edit titles and uncheck any groups to skip.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-1 py-2">
            {groups.map((group) => {
              const isExpanded = expandedPrefix === group.prefix;
              return (
                <div
                  key={group.prefix}
                  className={`rounded-md border ${group.enabled ? '' : 'opacity-50'}`}
                >
                  <div className="flex items-center gap-3 px-3 py-2">
                    <Checkbox
                      checked={group.enabled}
                      onCheckedChange={() => toggleGroup(group.prefix)}
                    />
                    {group.imageUrl ? (
                      <img
                        src={group.imageUrl}
                        alt=""
                        className="h-8 w-8 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">
                        -
                      </div>
                    )}
                    <Input
                      value={group.title}
                      onChange={(e) => updateGroupTitle(group.prefix, e.target.value)}
                      className="h-8 flex-1"
                      placeholder="Product title"
                      disabled={!group.enabled}
                    />
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground whitespace-nowrap"
                      onClick={() => setExpandedPrefix(isExpanded ? null : group.prefix)}
                    >
                      {group.skuIds.length} SKU{group.skuIds.length !== 1 ? 's' : ''}
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="border-t px-3 py-2 bg-muted/30">
                      <div className="flex flex-wrap gap-1.5">
                        {group.skus.map((sku) => (
                          <span
                            key={sku.id}
                            className="inline-flex items-center gap-1 rounded bg-background px-2 py-0.5 text-xs border"
                          >
                            <span className="font-mono">{sku.sku}</span>
                            {sku.size && (
                              <span className="text-muted-foreground">{sku.size}</span>
                            )}
                            {sku.color && (
                              <span className="text-muted-foreground">{sku.color}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {mutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              `Create ${enabledGroups.length} Product${enabledGroups.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
